import { useState } from "react";
import { Button } from "./ui/button";
import { Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from '../config/api';

import * as tests from "./health/testDefinitions";
import { ProgressChart } from "./health/ProgressChart";
import { OperationsSection } from "./health/OperationsSection";
import { FinancialsSection } from "./health/FinancialsSection";
import { CustomersSection } from "./health/CustomersSection";
import { SettingsSection } from "./health/SettingsSection";
import { AuthFlowsSection } from "./health/AuthFlowsSection";
import { CustomerFlowsSection } from "./health/CustomerFlowsSection";

const SystemHealthTab = () => {
  const [testResults, setTestResults] = useState({
    // Operations
    roles: [],
    team: [],
    assets: [],
    services: [],
    bookings: [],
    flows: [],
    // Financials
    revenue: [],
    payouts: [],
    account: [],
    payroll: [],
    stripeConnect: [],
    // Customers
    branding: [],
    waivers: [],
    reviews: [],
    checkin: [],
    leads: [],
    relations: [],
    // Settings
    profile: [],
    preferences: [],
    calendar: [],
    webhooks: [],
    apiKeys: [],
    activityLog: [],
    // Auth Flows
    ownerSignin: [],
    teamSignin: [],
    signup: [],
    // Customer Flows
    widgetCheckout: [],
    waiverFlow: [],
    qrCodeScanning: []
  });
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [testOrgInfo, setTestOrgInfo] = useState(null);

  const runHealthCheck = async () => {
    setLoading(true);
    toast.info('Running health tests...');
    
    try {
      const token = localStorage.getItem("jwtToken");
      
      // Helper function to call endpoint with fallback
      const callEndpoint = async (endpoint, testList) => {
        try {
          const response = await axios.post(`${API_URL}/health/test-suites/${endpoint}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return response;
        } catch (err) {
          // Return mock failed tests if endpoint not implemented
          return {
            data: {
              tests: testList.map(t => ({ 
                name: t.name, 
                status: 'fail', 
                message: 'Test suite not implemented yet' 
              })),
              summary: { passed: 0, failed: testList.length }
            }
          };
        }
      };

      // Run all test suites in parallel
      const [
        rolesResponse,
        teamResponse,
        assetsResponse,
        servicesResponse,
        bookingsResponse,
        flowsResponse,
        revenueResponse,
        payoutsResponse,
        accountResponse,
        payrollResponse,
        stripeConnectResponse,
        brandingResponse,
        waiversResponse,
        reviewsResponse,
        checkinResponse,
        leadsResponse,
        relationsResponse,
        profileResponse,
        preferencesResponse,
        calendarResponse,
        webhooksResponse,
        apiKeysResponse,
        activityLogResponse,
        ownerSigninResponse,
        teamSigninResponse,
        signupResponse,
        widgetCheckoutResponse,
        waiverFlowResponse,
        qrCodeScanningResponse
      ] = await Promise.all([
        // Operations
        callEndpoint('roles', tests.rolesTests),
        callEndpoint('team', tests.teamTests),
        callEndpoint('assets', tests.assetsTests),
        callEndpoint('services', tests.servicesTests),
        callEndpoint('bookings', tests.bookingsTests),
        callEndpoint('flows', tests.flowsTests),
        // Financials
        callEndpoint('revenue', tests.revenueTests),
        callEndpoint('payouts', tests.payoutsTests),
        callEndpoint('account', tests.accountTests),
        callEndpoint('payroll', tests.payrollTests),
        callEndpoint('stripe-connect', tests.stripeConnectTests),
        // Customers
        callEndpoint('branding', tests.brandingTests),
        callEndpoint('waivers', tests.waiversTests),
        callEndpoint('reviews', tests.reviewsTests),
        callEndpoint('checkin', tests.checkinTests),
        callEndpoint('leads', tests.leadsTests),
        callEndpoint('relations', tests.relationsTests),
        // Settings
        callEndpoint('profile', tests.profileTests),
        callEndpoint('preferences', tests.preferencesTests),
        callEndpoint('calendar', tests.calendarTests),
        callEndpoint('webhooks', tests.webhooksTests),
        callEndpoint('api-keys', tests.apiKeysTests),
        callEndpoint('activity-log', tests.activityLogTests),
        // Auth Flows
        callEndpoint('owner-signin', tests.ownerSigninTests),
        callEndpoint('team-signin', tests.teamSigninTests),
        callEndpoint('signup', tests.signupTests),
        // Customer Flows
        callEndpoint('widget-checkout', tests.widgetCheckoutTests),
        callEndpoint('waiver-flow', tests.waiverFlowTests),
        callEndpoint('qr-code-scanning', tests.qrCodeScanningTests)
      ]);
      
      setTestResults({
        // Operations
        roles: rolesResponse.data.tests,
        team: teamResponse.data.tests,
        assets: assetsResponse.data.tests,
        services: servicesResponse.data.tests,
        bookings: bookingsResponse.data.tests,
        flows: flowsResponse.data.tests,
        // Financials
        revenue: revenueResponse.data.tests,
        payouts: payoutsResponse.data.tests,
        account: accountResponse.data.tests,
        payroll: payrollResponse.data.tests,
        stripeConnect: stripeConnectResponse.data.tests,
        // Customers
        branding: brandingResponse.data.tests,
        waivers: waiversResponse.data.tests,
        reviews: reviewsResponse.data.tests,
        checkin: checkinResponse.data.tests,
        leads: leadsResponse.data.tests,
        relations: relationsResponse.data.tests,
        // Settings
        profile: profileResponse.data.tests,
        preferences: preferencesResponse.data.tests,
        calendar: calendarResponse.data.tests,
        webhooks: webhooksResponse.data.tests,
        apiKeys: apiKeysResponse.data.tests,
        activityLog: activityLogResponse.data.tests,
        // Auth Flows
        ownerSignin: ownerSigninResponse.data.tests,
        teamSignin: teamSigninResponse.data.tests,
        signup: signupResponse.data.tests,
        // Customer Flows
        widgetCheckout: widgetCheckoutResponse.data.tests,
        waiverFlow: waiverFlowResponse.data.tests,
        qrCodeScanning: qrCodeScanningResponse.data.tests
      });
      
      // Store org info if available
      if (rolesResponse.data.testOrg) {
        setTestOrgInfo({
          name: rolesResponse.data.testOrg,
          slug: rolesResponse.data.testOrgSlug
        });
      }
      
      setLastChecked(new Date());
      
      // Calculate totals
      const allResponses = [
        rolesResponse, teamResponse, assetsResponse, servicesResponse, bookingsResponse, flowsResponse,
        revenueResponse, payoutsResponse, accountResponse, payrollResponse, stripeConnectResponse,
        brandingResponse, waiversResponse, reviewsResponse, checkinResponse, leadsResponse, relationsResponse,
        profileResponse, preferencesResponse, calendarResponse, webhooksResponse, apiKeysResponse, activityLogResponse,
        ownerSigninResponse, teamSigninResponse, signupResponse,
        widgetCheckoutResponse, waiverFlowResponse, qrCodeScanningResponse
      ];
      
      const totalPassed = allResponses.reduce((sum, r) => sum + r.data.summary.passed, 0);
      const totalFailed = allResponses.reduce((sum, r) => sum + r.data.summary.failed, 0);
      
      if (totalFailed === 0) {
        toast.success(`✅ All ${totalPassed} tests passed!`);
      } else {
        toast.error(`${totalFailed} test(s) failed, ${totalPassed} passed`);
      }
    } catch (error) {
      console.error("Health check error:", error);
      toast.error("Failed to run tests");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Health</h2>
          <p className="text-gray-600 text-sm mt-1">Test application functionality</p>
        </div>
        <Button
          onClick={runHealthCheck}
          disabled={loading}
          className="bg-cyan-600 hover:bg-cyan-700"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Activity className="h-4 w-4 mr-2" />
              Run Health Check
            </>
          )}
        </Button>
      </div>

      {/* Last Checked & Progress Chart */}
      {lastChecked && (
        <div>
          <p className="text-sm text-gray-500 mb-4">
            Last checked: {lastChecked.toLocaleTimeString()}
          </p>
          <ProgressChart testResults={testResults} testOrgInfo={testOrgInfo} />
        </div>
      )}

      {/* Test Sections */}
      <OperationsSection testResults={testResults} />
      <FinancialsSection testResults={testResults} />
      <CustomersSection testResults={testResults} />
      <SettingsSection testResults={testResults} />
      <AuthFlowsSection testResults={testResults} />
      <CustomerFlowsSection testResults={testResults} />
    </div>
  );
};

export default SystemHealthTab;
